/**
 * Credential Types
 *
 * Strongly-typed credential data structures for each auth provider
 */

import { z } from 'zod';

export type AuthProvider =
  | 'basic'
  | 'google'
  | 'google_oauth'
  | 'github'
  | 'microsoft'
  | 'api_key'
  | 'twitter'
  | 'custom'
  | 'api_login'
  | 'api_basic';

export type CredentialRole = 'admin' | 'user' | 'viewer' | 'custom';

/**
 * Custom HTTP headers for browser automation.
 * Stored encrypted alongside credential data.
 * Useful for bypassing bot detection (e.g., User-Agent, Accept-Language).
 */
export type CustomHttpHeaders = Record<string, string>;

/**
 * HTTP Basic Authentication credentials for browser-level auth popups.
 * Passed to Playwright via browser.newContext({ httpCredentials }).
 */
export type HttpCredentials = {
  username: string;
  password: string;
};

// Individual credential data types

export type BasicAuthCredential = {
  username: string;
  password: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type GoogleCredential = {
  email: string;
  password: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

/**
 * Google OAuth "minted session" credential: instead of driving the app's Google
 * sign-in UI (impossible for an agent — third-party origin, bot detection), we
 * hold a long-lived refresh token for the test account and exchange it for a
 * fresh `id_token` at run time, then seed the result into storage state.
 *
 * `clientId` / `clientSecret` are the CUSTOMER's OAuth client — the one their
 * app validates the `id_token` `aud` claim against. They are required and have
 * no default: a token minted by any other client is rejected by the target app,
 * so falling back to a platform-owned client would only fail later and less
 * legibly.
 */
export type GoogleOauthCredential = {
  clientId: string;
  clientSecret: string;
  refreshToken: string;
  /** Extra scopes beyond `openid email profile`. */
  scopes?: string[];
  /** Minted-token cache. Same shape/semantics as ApiLoginCredential's cachedToken. */
  cachedIdToken?: string;
  cachedAccessToken?: string;
  cachedProfile?: GoogleOauthProfile;
  /**
   * The provider's raw token-endpoint response. Cached alongside the tokens
   * because session seeds may template over ANY of its fields
   * (`{{response.<path>}}`) — restoring only the named tokens would silently
   * break those placeholders on every cache hit.
   */
  cachedResponse?: Record<string, unknown>;
  cachedTokenExpiresAt?: string;
  cachedTokenObtainedAt?: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

/** OpenID Connect userinfo claims we surface to session-seed templates. */
export type GoogleOauthProfile = {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  hd?: string;
};

export type GithubCredential = {
  username: string;
  password: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type MicrosoftCredential = {
  email: string;
  password: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type ApiKeyCredential = {
  key: string;
  prefix?: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type TwitterCredential = {
  username: string;
  password: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type ApiLoginCredential = {
  loginFields: Record<string, string>;
  /** Encrypted headers sent only to the token endpoint (for example SOAPAction). */
  tokenRequestHeaders?: Record<string, string>;
  cachedToken?: string;
  cachedTokenExpiresAt?: string;
  cachedTokenObtainedAt?: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type ApiBasicCredential = {
  username: string;
  password: string;
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

export type CustomCredential = Record<string, any> & {
  customHeaders?: CustomHttpHeaders;
  httpCredentials?: HttpCredentials;
};

// Union type for all credential data
export type CredentialData =
  | { provider: 'basic'; data: BasicAuthCredential }
  | { provider: 'google'; data: GoogleCredential }
  | { provider: 'google_oauth'; data: GoogleOauthCredential }
  | { provider: 'github'; data: GithubCredential }
  | { provider: 'microsoft'; data: MicrosoftCredential }
  | { provider: 'api_key'; data: ApiKeyCredential }
  | { provider: 'twitter'; data: TwitterCredential }
  | { provider: 'api_login'; data: ApiLoginCredential }
  | { provider: 'api_basic'; data: ApiBasicCredential }
  | { provider: 'custom'; data: CustomCredential };

/**
 * Login validity tracking data for session duration prediction
 */
export type LoginValidityInfo = {
  /** When a login was last attempted */
  lastLoginAt: Date | null;
  /** When a login last succeeded */
  lastLoginSucceededAt: Date | null;
  /** When the session was detected as expired (requiring re-login) */
  nextLoginRequiredAt: Date | null;
  /** Calculated session validity duration in minutes */
  loginValidityDurationMinutes: number | null;
  /** Estimated session expiry based on last login + known validity */
  estimatedExpiresAt: Date | null;
};

/**
 * HeaderExtractor: declarative way to derive an HTTP header value from a
 * credential's saved storage state (Playwright cookies + localStorage +
 * extracted IndexedDB). Materialized at request time by the executor and
 * merged into the same `extraHeaders` map as `customHeaders`.
 *
 * Modern SPAs (Firebase Auth, Auth0, Clerk, …) stash JWTs in localStorage or
 * IndexedDB and rely on page JS to forward them as request headers. Playwright
 * APIRequestContext only carries cookies, so those headers go missing when we
 * fetch through the browser context. Extractors close that gap.
 */
export const tokenRefreshPolicySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('firebase'),
    refreshTokenPath: z.string().default('$.value.stsTokenManager.refreshToken'),
    expirationPath: z.string().default('$.value.stsTokenManager.expirationTime'),
    accessTokenPath: z.string().default('$.value.stsTokenManager.accessToken'),
    apiKeyPath: z.string().default('$.value.apiKey'),
  }),
]);

export type TokenRefreshPolicy = z.infer<typeof tokenRefreshPolicySchema>;

const localStorageSourceSchema = z.object({
  kind: z.literal('localStorage'),
  origin: z.string().min(1),
  key: z.string().min(1),
  jsonPath: z.string().optional(),
});

const indexedDBSourceSchema = z.object({
  kind: z.literal('indexedDB'),
  database: z.string().min(1),
  store: z.string().min(1),
  key: z.string().min(1),
  jsonPath: z.string().min(1),
});

const cookieSourceSchema = z.object({
  kind: z.literal('cookie'),
  domain: z.string().min(1),
  name: z.string().min(1),
});

export const headerExtractorSourceSchema = z.discriminatedUnion('kind', [
  localStorageSourceSchema,
  indexedDBSourceSchema,
  cookieSourceSchema,
]);

export const headerExtractorSchema = z.object({
  name: z.string().min(1),
  header: z.string().min(1).optional(),
  prefix: z.string().optional(),
  source: headerExtractorSourceSchema,
  refresh: tokenRefreshPolicySchema.optional(),
});

export type HeaderExtractorSource = z.infer<typeof headerExtractorSourceSchema>;
export type HeaderExtractor = z.infer<typeof headerExtractorSchema>;

/**
 * SessionSeed: the write-direction mirror of {@link headerExtractorSchema}.
 *
 * An extractor reads a value OUT of a captured storage state to build a request
 * header. A seed writes a templated value INTO a synthesized storage state, so
 * the browser boots already authenticated without ever driving a login UI.
 * Same `kind` vocabulary on purpose — one mental model, one JSON editor.
 *
 * Cypress' documented Google recipe expressed as a seed:
 *
 *   { name: 'googleSession',
 *     target: { kind: 'localStorage', origin: 'https://app.acme.com', key: 'googleCypress' },
 *     valueTemplate: '{"token":"{{id_token}}","user":{"googleId":"{{profile.sub}}", ...}}' }
 *
 * @see https://docs.cypress.io/app/guides/authentication-testing/google-authentication
 */
export const sessionSeedTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('localStorage'),
    origin: z.string().min(1),
    key: z.string().min(1),
  }),
  // NB: no `sessionStorage` target. Playwright's storage state does not carry
  // sessionStorage and browser-core has no restore path for it, so such a seed
  // would be silently dropped. Adding it means teaching the browser client to
  // seed it via addInitScript first.
  z.object({
    kind: z.literal('cookie'),
    domain: z.string().min(1),
    name: z.string().min(1),
    path: z.string().default('/'),
    secure: z.boolean().default(true),
    httpOnly: z.boolean().default(false),
    sameSite: z.enum(['Strict', 'Lax', 'None']).default('Lax'),
  }),
  z.object({
    kind: z.literal('indexedDB'),
    /**
     * Required, unlike the read-direction extractor's indexedDB source.
     * IndexedDB is origin-scoped and Playwright cannot restore it from storage
     * state alone — `restoreIndexedDBFromStorageState` has to navigate to an
     * origin first, and it derives that origin from `origins[]`/`cookies[]`. An
     * IndexedDB-only seed carries neither, so without an explicit origin the
     * restore silently no-ops. Same reason `sessionStorage` is absent above:
     * a target that can't actually be restored must not be accepted.
     */
    origin: z.string().min(1),
    database: z.string().min(1),
    store: z.string().min(1),
    key: z.string().min(1),
  }),
]);

export const sessionSeedSchema = z.object({
  name: z.string().min(1),
  target: sessionSeedTargetSchema,
  /**
   * The value written to `target`. `{{placeholder}}` tokens are substituted
   * from the minted session — see MINTED_SESSION_PLACEHOLDERS in
   * apps/api/src/credentials/minted-session/seeds.ts for the full list.
   * Substituted values are JSON-escaped when the template parses as JSON.
   */
  valueTemplate: z.string().min(1),
});

export type SessionSeedTarget = z.infer<typeof sessionSeedTargetSchema>;
export type SessionSeed = z.infer<typeof sessionSeedSchema>;

/**
 * Cross-seed rule: when any IndexedDB seed is present, every localStorage and
 * IndexedDB seed must declare the same origin.
 *
 * Playwright's storage state carries ONE global IndexedDB payload, and
 * `restoreIndexedDBFromStorageState` restores all of it under `origins[0]` —
 * there is no way to say "this database belongs to that origin". With a mixed
 * set the database lands on whichever origin happens to be listed first, and
 * several IndexedDB origins collapse onto one. Refusing is the only honest
 * option short of teaching browser-core to group IndexedDB by origin.
 *
 * localStorage-only sets are unconstrained: Playwright restores those
 * per-origin natively.
 *
 * Lives here, in the types package, so save-time validation (API + editor) and
 * run-time rendering all enforce one rule from one definition rather than three
 * drifting copies.
 *
 * @returns a human-readable explanation, or null when the set is fine.
 */
export function findSessionSeedOriginConflict(
  seeds: ReadonlyArray<{ target: { kind: string; origin?: string } }>
): string | null {
  const origins = new Set<string>();
  let hasIndexedDb = false;

  for (const seed of seeds) {
    const { kind, origin } = seed.target;
    if (kind === 'indexedDB') {
      hasIndexedDb = true;
      if (origin) origins.add(origin);
    } else if (kind === 'localStorage') {
      if (origin) origins.add(origin);
    }
  }

  if (!hasIndexedDb || origins.size <= 1) return null;
  return (
    `IndexedDB seeds require every localStorage and IndexedDB seed to share one origin, ` +
    `but ${origins.size} were declared (${[...origins].sort().join(', ')}). Playwright ` +
    `restores a single global IndexedDB payload under one origin, so a mixed set would ` +
    `silently restore the database under the wrong one.`
  );
}

/**
 * The array form, with the cross-seed rule attached. Use this — not
 * `z.array(sessionSeedSchema)` — wherever a seed set is accepted, so a config
 * that run time would refuse can never be saved in the first place.
 */
export const sessionSeedsSchema = z
  .array(sessionSeedSchema)
  // Bound lives here rather than at each call site: `superRefine` yields a
  // ZodEffects, which has no `.max()`, so a caller cannot add it afterwards.
  .max(50)
  .superRefine((seeds, ctx) => {
    const conflict = findSessionSeedOriginConflict(seeds);
    if (conflict) ctx.addIssue({ code: z.ZodIssueCode.custom, message: conflict });
  });

/**
 * Typed view of credential.metadata. Existing fields (headerName / token*)
 * predate extractors and remain supported. `headerExtractors` is the new
 * generalized lane.
 */
export const credentialMetadataSchema = z
  .object({
    headerName: z.string().optional(),
    tokenHeaderName: z.string().optional(),
    tokenHeaderPrefix: z.string().optional(),
    headerExtractors: z.array(headerExtractorSchema).optional(),
    /**
     * Per-credential override of the property's `sessionSeeds`. Where the app
     * keeps its session is normally an app-level fact, so the property is the
     * primary home; this exists for the case where one test user's session
     * lands somewhere different.
     */
    sessionSeeds: sessionSeedsSchema.optional(),
  })
  .passthrough();

export type CredentialMetadata = z.infer<typeof credentialMetadataSchema>;

// Extract email from credential data if available
export function extractEmail(provider: AuthProvider, data: any): string | null {
  switch (provider) {
    case 'basic':
      return data.username || null;
    case 'google':
    case 'microsoft':
      return data.email || null;
    // Nothing identifies the account until a token has been minted at least
    // once — the refresh token itself is opaque.
    case 'google_oauth':
      return data.cachedProfile?.email || null;
    case 'github':
    case 'twitter': {
      // May be username or email
      const val = data.username || null;
      return val && val.includes('@') ? val : null;
    }
    case 'api_login':
      return data.loginFields?.username || data.loginFields?.email || null;
    case 'api_basic': {
      const val = data.username || null;
      return val && val.includes('@') ? val : null;
    }
    default:
      return null;
  }
}
